import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@/components/ui/toast'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { SettingsDrawerHeader } from '@/components/settings/SettingsDrawerHeader'
import {
  useResendUserWelcomeEmail,
  useUser,
  useUserPermissions,
} from '@/hooks/useUsers'
import { getApiErrorMessage } from '@/services/api'
import {
  defaultPermissionsForRole,
  type PortalUser,
  type UserRole,
} from '@/types'
import { UserForm } from './UserForm'

export type UserFormMode = 'create' | 'edit' | 'view'

interface UserFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // null/undefined => create; a user => edit/view.
  user?: PortalUser | null
  mode?: UserFormMode
  // The signed-in viewer's role - Manager cannot create/edit Admin users.
  viewerRole: UserRole | undefined
  // When viewing, show Edit if the viewer may manage this row.
  canEdit?: boolean
  // Disable demote/deactivate when this is the last active Admin (FE guard).
  protectLastAdmin?: boolean
  // Switch from view → edit without closing the dialog shell.
  onRequestEdit?: () => void
  // Cancel can return an edit opened from view back to the read-only detail.
  onCancel?: () => void
}

// 2.11.2 - Create / Edit / View user, with inline module permission control
// (2.11.3). Outer shell fetches the detail + saved matrix in edit/view mode
// and only mounts the form body once data is ready.
export function UserFormDialog({
  open,
  onOpenChange,
  user,
  mode = user ? 'edit' : 'create',
  viewerRole,
  canEdit = false,
  protectLastAdmin = false,
  onRequestEdit,
  onCancel,
}: UserFormDialogProps) {
  const { t } = useTranslation('settings')
  const isCreate = mode === 'create'
  const isView = mode === 'view'
  const isEdit = mode === 'edit'
  const userId = !isCreate ? user?.id : undefined

  const resendWelcomeEmail = useResendUserWelcomeEmail()
  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
  } = useUser(open ? userId : undefined)
  const { data: existingPermissions, isLoading: permissionsLoading } =
    useUserPermissions(open ? userId : undefined)

  const resolvedUser = detail ?? user ?? null
  const ready =
    isCreate ||
    (!!resolvedUser &&
      !!existingPermissions &&
      !detailLoading &&
      !permissionsLoading)
  // Resend only when the viewer may manage this row (not for Admin targets as Manager).
  const canResendCredentials = isView && canEdit && !!resolvedUser?.email

  const title = isView
    ? t('users.dialog.viewTitle')
    : isEdit
      ? t('users.dialog.editTitle')
      : t('users.dialog.createTitle')

  const onResendCredentials = async () => {
    if (!resolvedUser?.id) return
    try {
      const res = await resendWelcomeEmail.mutateAsync(resolvedUser.id)
      toast({
        variant: 'success',
        title: res.message || t('users.dialog.resendCredentialsSuccess'),
      })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('users.dialog.resendCredentialsError')),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        side='right'
        showCloseButton={false}
        className='w-full max-w-[720px] min-h-0 max-h-dvh *:min-w-0 [&>button]:right-6 [&>button]:top-4 [&>button]:rounded-xl [&>button]:bg-[#f4f5f8] [&>button]:p-2 [&>button]:opacity-100 [&>button]:text-[#555b6a]'
        onOpenAutoFocus={isView ? e => e.preventDefault() : undefined}
      >
        <SettingsDrawerHeader title={title} titleClassName='text-base' />

        {!ready ? (
          <div className='flex flex-1 flex-col gap-4 bg-[#f4f5f8] px-6 py-6'>
            {detailError ? (
              <p className='text-sm text-destructive'>{t('users.loadError')}</p>
            ) : (
              <>
                <Skeleton className='h-[240px] w-full rounded-2xl' />
                <Skeleton className='h-[320px] w-full rounded-2xl' />
              </>
            )}
          </div>
        ) : (
          <UserForm
            key={`${mode}-${resolvedUser?.id ?? 'create'}`}
            user={resolvedUser}
            viewerRole={viewerRole}
            readOnly={isView}
            canEdit={canEdit}
            protectLastAdmin={protectLastAdmin}
            onRequestEdit={onRequestEdit}
            canResendCredentials={canResendCredentials}
            resendPending={resendWelcomeEmail.isPending}
            onResendCredentials={onResendCredentials}
            onCancel={onCancel}
            initialMatrix={
              !isCreate && existingPermissions
                ? existingPermissions
                : defaultPermissionsForRole('Operator')
            }
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

export default UserFormDialog
